<script setup lang="ts">
import '@/assets/scss/form.scss'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant'
import { yupResolver } from '@primevue/forms/resolvers/yup'
import { useConfirm } from 'primevue'
import { useToast } from 'primevue/usetoast'
import { ref, watch } from 'vue'
import TransactionAPI from '../constants/transaction.api.uri.const'
import {
  initialValuesWithdrawProcess,
  schemaWithdrawProcess
} from '../helper/validateTransactionSchema'
import { Icon } from '@iconify/vue/dist/iconify.js'

const statusOptions = [
  { name: 'Chờ xử lý', code: 'pending' },
  { name: 'Đã xử lý', code: 'paid' },
  { name: 'Từ chối', code: 'rejected' },
]
const confirm = useConfirm()

interface Props {
  account: object | undefined
}

const formRef = ref()
const props = defineProps<Props>()

const emit = defineEmits(['update:closeModal'])
const toast = useToast()
const loading = ref(false)
const initialValues = ref(initialValuesWithdrawProcess(props.account))

const resolver = yupResolver(schemaWithdrawProcess)

const requestWithdrawProcess = async (data: any) => {
  loading.value = true
  try {
    const res = await APIClient.put(
      TransactionAPI.transactions.withdrawal.replace(':id', data?.id),
      data,
    )

    if (res.data.code === 200) {
      //close modal
      loading.value = false
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Xử lý giao dịch thành công',
        life: 3000,
      })
      emit('update:closeModal')
      return
    }
  } catch (e: any) {
    loading.value = false
    const errorMessage = e.response?.data?.message || 'Đã có lỗi xảy ra'
    toast.add({ severity: 'error', summary: 'Error', detail: errorMessage, life: 3000 })
  }
}

const onFormSubmit = ({ values, valid }: any) => {
  console.log('values', values, valid)

  if (!valid) {
    return
  }
  confirm.require({
    message: 'Bạn có muốn thao tác xử lý giao dịch?',
    header: 'Xác nhận',
    // icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: () => {
      const formData = {
        id: values.id,
        status: values.status,
        note: values.note,
      }
      requestWithdrawProcess(formData)
    },
    reject: () => {
      // toast.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    },
  })
}

const selectStatus = ref({
  status:
    initialValues.value?.status === 'pending'
      ? statusOptions[0].code
      : initialValues.value?.status === 'paid'
        ? statusOptions[1].code
        : statusOptions[2].code,
})

watch(
  () => initialValues.value?.status,
  (newVal) => {
    selectStatus.value.status =
      newVal === 'pending'
        ? statusOptions[0].code
        : newVal === 'paid'
          ? statusOptions[1].code
          : statusOptions[2].code
  },
)

const handleAction = (action: string) => {
  console.log('action', action)
  if (action === 'paid') {
    formRef.value.setFieldValue('status', action)
  } else if (action === 'rejected') {
    formRef.value.setFieldValue('status', action)
  }
  formRef.value.submit()
}
</script>

<template>
  <div class="pt-3">
    <Form
      v-slot="$form"
      ref="formRef"
      :initial-values="initialValues"
      :resolver="resolver"
      @submit="onFormSubmit"
      class="flex flex-col w-full"
    >
      <InputText name="id" type="hidden" class="form-control" />
      <InputText name="status" type="hidden" class="form-control" />
      <div class="flex flex-col flex-3">
        <div class="flex mb-3 gap-3">
          <div class="flex-1 text-right">Tài khoản:</div>
          <div class="flex-2">
            <InputText name="full_name" type="text" class="form-control" disabled />
          </div>
        </div>
        <div class="flex mb-3 gap-3">
          <div class="flex-1 text-right">Số tiền:</div>
          <div class="flex-2">
            <InputNumber
              name="amount"
              class="form-control"
              inputId="minmax-buttons"
              suffix=" đ"
              mode="decimal"
              locale="vi-VN"
              :min="0"
              :max="1000000000"
              fluid
              disabled
            />
            <!-- <span class="font-italic text-[12px] text-red-500">{{numHelper($form?.amount?.value).numberToVietnameseText({unit: 'đồng'})}}</span> -->
          </div>
        </div>
        <div class="flex mb-3 gap-3">
          <div class="flex-1 text-right">Mã giao dịch:<br />(Bank Transaction Content)</div>
          <div class="flex-2">
            <InputText name="transaction_code" type="text" class="form-control" disabled />
          </div>
        </div>
        <!-- <div class="flex mb-3 gap-3">
          <div class="flex-1 text-right">Trạng thái:</div>
          <div class="flex-2">
            <Select
              name="status"
              v-model="selectStatus.status"
              :options="statusOptions"
              optionLabel="name"
              optionValue="code"
              placeholder="Chọn trạng thái"
              class="form-control"
            />
            <span class="italic text-[12px] text-red-500" v-if="$form.status?.invalid">
              {{ $form.status.error.message }}
            </span>
          </div>
        </div> -->
        <div class="flex mb-3 gap-3">
          <div class="flex-1 text-right">Ghi chú:</div>
          <div class="flex-2">
            <InputText name="note" type="text" class="form-control" />
            <span class="italic text-[12px] text-red-500" v-if="$form.note?.invalid">
              {{ $form.note.error.message }}
            </span>
          </div>
        </div>
        <div class="flex mb-3 gap-3">
          <div class="flex-2 flex justify-center">
            <!-- <button type="submit" class="btn btn-cs primary lg">Từ chối</button>
            <button type="submit" class="btn btn-cs primary lg">Xác nhận</button> -->
            <div class="flex gap-2 pl-4 ml-4">
              <button
                :class="['btn flex items-center gap-2', loading ? 'btn-disabled' : 'btn-success ']"
                @click="handleAction('paid')"
                :disabled="loading"
              >
                <Icon icon="mdi:check-all" width="20" height="20" />
                Phê duyệt
                <Icon icon="eos-icons:three-dots-loading" width="24" height="24" v-if="loading && $form.status.value === 'paid'" />
              </button>
              <button
                :class="['btn  flex items-center gap-2', loading ? 'btn-disabled' : 'btn-danger']"
                @click="handleAction('rejected')"
                :disabled="loading"
              >
                <Icon icon="healthicons:i-documents-denied" width="20" height="20" />
                Từ chối
                <Icon icon="eos-icons:three-dots-loading" width="24" height="24" v-if="loading && $form.status.value === 'rejected'" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Form>
  </div>
</template>
